'use client';

/**
 * Production coachmark: a floating-material bubble (Radix Popover on a
 * virtual anchor) with a speech arrow, pointing at a spotlighted target.
 * The scrim dims everything around the target through a four-piece cutout —
 * targets are never z-lifted (arbitrary app targets sit inside their own
 * stacking contexts), the hole in the scrim leaves them fully visible and
 * clickable — and a ring overlay draws the spotlight around the target rect.
 * Everything portals to document.body so transformed ancestors (RouteFade,
 * chat FadeIn) can never capture the fixed positioning.
 */

import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/button';
import { TOTAL_STEPS, type CoachmarkCopy } from '@/lib/tour/copy';
import { cn } from '@/lib/utils';

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SPOTLIGHT_PADDING = 4;

function useAnchorRect(anchor: HTMLElement | null): AnchorRect | null {
  // The rect is stored with the element it was measured on: when the anchor
  // swaps (navigation replaces the sidebar mid-beat), a stale rect must never
  // paint a frame at the previous element's position.
  const [measured, setMeasured] = React.useState<{
    el: HTMLElement;
    rect: AnchorRect;
  } | null>(null);

  React.useEffect(() => {
    if (!anchor) {
      setMeasured(null);
      return;
    }

    let frame = 0;
    const measure = () => {
      frame = 0;
      // A detached element measures as a zero rect at the origin (the
      // top-left flight). FREEZE the last good rect instead of dropping it:
      // the driver holds the element briefly through navigation remounts,
      // and the coachmark should hold its position rather than blink.
      if (!anchor.isConnected) return;
      const next = anchor.getBoundingClientRect();
      if (next.width === 0 && next.height === 0) return;
      setMeasured((current) =>
        current &&
        current.el === anchor &&
        current.rect.top === next.top &&
        current.rect.left === next.left &&
        current.rect.width === next.width &&
        current.rect.height === next.height
          ? current
          : {
              el: anchor,
              rect: {
                top: next.top,
                left: next.left,
                width: next.width,
                height: next.height,
              },
            }
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    measure();
    const observer = new ResizeObserver(schedule);
    observer.observe(anchor);
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    // Fallback for layout shifts neither listener sees (e.g. sibling
    // insertions moving the target without resizing it).
    const interval = window.setInterval(schedule, 300);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      window.clearInterval(interval);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [anchor]);

  // Only a rect measured on the CURRENT anchor counts.
  return anchor && measured?.el === anchor ? measured.rect : null;
}

/** Padded spotlight box, intersected with the viewport: a target taller or
 * wider than the screen (e.g. the filmstrip) rings only its visible part. */
function spotlightBox(rect: AnchorRect) {
  const top = Math.max(rect.top - SPOTLIGHT_PADDING, 0);
  const left = Math.max(rect.left - SPOTLIGHT_PADDING, 0);
  const bottom = Math.min(
    rect.top + rect.height + SPOTLIGHT_PADDING,
    window.innerHeight
  );
  const right = Math.min(
    rect.left + rect.width + SPOTLIGHT_PADDING,
    window.innerWidth
  );
  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

/** Scrim with a true hole over the target: clicks inside pass through.
 * `blockTarget` covers the cutout with a transparent fifth piece so the
 * target stays visible and ringed but cannot be clicked. */
function CoachmarkScrim({
  cutout,
  blockTarget = false,
}: {
  cutout: AnchorRect | null;
  blockTarget?: boolean;
}) {
  const box = cutout ? spotlightBox(cutout) : null;
  const pieces: React.CSSProperties[] = box
    ? [
        { top: 0, left: 0, right: 0, height: Math.max(0, box.top) },
        {
          top: box.top,
          left: 0,
          width: Math.max(0, box.left),
          height: box.height,
        },
        {
          top: box.top,
          left: box.left + box.width,
          right: 0,
          height: box.height,
        },
        { top: box.top + box.height, left: 0, right: 0, bottom: 0 },
      ]
    : [{ top: 0, left: 0, right: 0, bottom: 0 }];

  return (
    <div aria-hidden className="contents">
      {pieces.map((style, index) => (
        <div
          key={index}
          className="pointer-events-auto fixed z-40 bg-black/50 animate-in fade-in-0 duration-100"
          style={style}
        />
      ))}
      {blockTarget && box && (
        <div
          className="pointer-events-auto fixed z-40 bg-transparent"
          style={{
            top: box.top,
            left: box.left,
            width: box.width,
            height: box.height,
          }}
        />
      )}
    </div>
  );
}

function SpotlightRing({ rect }: { rect: AnchorRect }) {
  return (
    <div
      aria-hidden
      // z-[60]: Radix popover/menu content portals at z-50 after this ring,
      // so at z-50 the ring would paint behind a spotlit menu.
      className="pointer-events-none fixed z-[60] rounded-md ring-[3px] ring-ring/60 animate-in fade-in-0 duration-100"
      style={spotlightBox(rect)}
    />
  );
}

export function CoachmarkBody({
  copy,
  onNext,
  onBack,
  onSkip,
}: {
  copy: CoachmarkCopy;
  onNext: () => void;
  onBack?: () => void;
  onSkip: () => void;
}) {
  return (
    <>
      <div className="px-4 pt-3.5 pb-3">
        <div className="text-xs font-medium text-fg-light">
          {copy.step} of {TOTAL_STEPS}
        </div>
        <div className="mt-1.5 text-base font-semibold text-fg">
          {copy.title}
        </div>
        <p className="mt-1 text-sm leading-relaxed text-fg-secondary">
          {copy.body}
        </p>
      </div>
      <div className="flex items-center justify-between border-t border-line-soft px-3 py-2">
        <Button variant="tertiary" size="xs" onClick={onSkip}>
          Skip tour
        </Button>
        <div className="flex items-center gap-2">
          {onBack && (
            <Button variant="secondary" size="sm" onClick={onBack}>
              Back
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={onNext}>
            {copy.cta}
          </Button>
        </div>
      </div>
    </>
  );
}

/** Centered bubble for stages whose anchor is not on screen (no scrim, so
 * the user can navigate to wherever the highlighted control lives). */
export function CoachmarkFallback({
  copy,
  onNext,
  onBack,
  onSkip,
}: {
  copy: CoachmarkCopy;
  onNext: () => void;
  onBack?: () => void;
  onSkip: () => void;
}) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-8 z-50 flex justify-center px-4">
      <div className="pointer-events-auto w-80 rounded-md border border-line bg-popover text-popover-foreground shadow-floating animate-in fade-in-0 zoom-in-95">
        <CoachmarkBody copy={copy} onNext={onNext} onBack={onBack} onSkip={onSkip} />
      </div>
    </div>,
    document.body
  );
}

export interface CoachmarkProps {
  copy: CoachmarkCopy;
  /** Resolved visible target; null falls back to the centered bubble. */
  anchor: HTMLElement | null;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Suppress the dimming scrim (target lives under another overlay). */
  noScrim?: boolean;
  /** False blocks clicks on the spotlit target (explanation-only beats). */
  interactive?: boolean;
  onNext: () => void;
  /** Step back to the previous beat; absent on beats with no back target. */
  onBack?: () => void;
  onSkip: () => void;
}

export function Coachmark({
  copy,
  anchor,
  side = 'bottom',
  align = 'center',
  noScrim = false,
  interactive = true,
  onNext,
  onBack,
  onSkip,
}: CoachmarkProps) {
  const rect = useAnchorRect(anchor);

  // Radix re-reads this rect on its own schedule (scroll, animation frames),
  // including the window where a navigation just detached the anchor — a
  // detached node measures as a zero rect at the origin and the popover
  // would fly to the top-left for a frame. Serve the last good measurement
  // whenever the live read is unusable.
  const lastGoodRectRef = React.useRef<AnchorRect | null>(null);
  if (rect) lastGoodRectRef.current = rect;
  const virtualRef = React.useMemo(
    () => ({
      current: {
        getBoundingClientRect: () => {
          const live = anchor?.isConnected
            ? anchor.getBoundingClientRect()
            : null;
          if (live && (live.width > 0 || live.height > 0)) return live;
          const last = lastGoodRectRef.current;
          return last
            ? new DOMRect(last.left, last.top, last.width, last.height)
            : new DOMRect();
        },
        contextElement: anchor ?? undefined,
      },
    }),
    [anchor]
  );

  if (!anchor) {
    return (
      <CoachmarkFallback
        copy={copy}
        onNext={onNext}
        onBack={onBack}
        onSkip={onSkip}
      />
    );
  }
  // Anchor resolved but not yet measured (first effect pass, or the anchor
  // element just swapped during a navigation remount): hold the last good
  // rect instead of unmounting — a null render here blinks the whole
  // coachmark out for a frame. Render nothing only when no position has
  // ever been measured.
  const displayRect = rect ?? lastGoodRectRef.current;
  if (!displayRect) return null;

  return (
    <>
      {typeof document !== 'undefined'
        ? createPortal(
            <>
              {!noScrim && (
                <CoachmarkScrim cutout={displayRect} blockTarget={!interactive} />
              )}
              <SpotlightRing rect={displayRect} />
            </>,
            document.body
          )
        : null}
      <PopoverPrimitive.Root open>
        <PopoverPrimitive.Anchor virtualRef={virtualRef} />
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side={side}
            align={align}
            sideOffset={10}
            collisionPadding={12}
            onOpenAutoFocus={(event) => event.preventDefault()}
            className={cn(
              // pointer-events-auto: a modal Radix menu can set
              // pointer-events:none on <body> while a menu-item is spotlit.
              'pointer-events-auto z-50 w-80 origin-(--radix-popover-content-transform-origin) rounded-md border bg-popover p-0 text-popover-foreground outline-hidden',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
            )}
          >
            <CoachmarkBody
              copy={copy}
              onNext={onNext}
              onBack={onBack}
              onSkip={onSkip}
            />
            {/* speech-bubble arrow: bordered square rotated under the content */}
            <PopoverPrimitive.Arrow asChild>
              <div className="relative -top-px size-2.5 -translate-y-1/2 rotate-45 border-r border-b border-line bg-popover" />
            </PopoverPrimitive.Arrow>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </>
  );
}
